import React from "react";
import U2000ErrorTooltip from "../feedback/U2000ErrorTooltip";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../reducer/formReducer/U2000FormState";

interface U2000CheckBoxArrayProps<T extends Record<string, any>> {
  formStateHandlers: U2000FormStateHandlers<T>;
  fieldName: string;
  value: any;
  label: string;
  getValidations?: () => void;
  readonly?: boolean;
}

// Utilitaire pour accéder à une propriété imbriquée d'un objet à partir d'un chemin (ex: "user.name")
function getNestedValue<T extends Record<string, any>>(formStateHandlers: U2000FormStateHandlers<T>, fieldName: string): any {
  const data = formStateHandlers.getData() as Record<string, any>;
  return fieldName.split(".").reduce((acc: any, part: string) => (acc !== undefined && acc !== null ? acc[part] : undefined), data);
}

const U2000CheckBoxArray = <T extends Record<string, any>>({ formStateHandlers, fieldName, value, label, readonly = false }: U2000CheckBoxArrayProps<T>) => {
  // fieldName: le nom du champ array dans le formState
  // value: la valeur unique de cette checkbox

  const disabled = readonly || formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const checked = formStateHandlers.getData() ? (getNestedValue(formStateHandlers, fieldName) || []).includes(value) : false;
  const checkboxId = `${fieldName}-${value}`;
  const hasError = !!formStateHandlers.getErrors()[fieldName] && (formStateHandlers.getTouched()[fieldName] || formStateHandlers.isSubmitted());
  const errorMsg = hasError ? formStateHandlers.getErrors()[fieldName] : undefined;

  function handleCheck(e: React.ChangeEvent<HTMLInputElement>) {
    if (!disabled) {
      const currentArray: any[] = getNestedValue(formStateHandlers, fieldName) || [];
      let newArray: any[];
      if (e.target.checked) {
        newArray = [...currentArray, value];
      } else {
        newArray = currentArray.filter((v) => v !== value);
      }
      if (formStateHandlers.onChange) {
        formStateHandlers.onChange(
          {
            ...e,
            target: {
              ...e.target,
              value: newArray as unknown as string,
              id: fieldName,
            },
          },
          newArray
        );
      }
    }
  }

  function handleBlur() {
    if (formStateHandlers.onBlur) {
      formStateHandlers.onBlur({
        target: {
          id: fieldName,
        },
      } as React.FocusEvent<HTMLInputElement>);
    }
  }

  const content = (
    <div className="flex h-full items-center gap-3">
      <input
        id={checkboxId}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={handleCheck}
        className={`peer absolute opacity-0 h-5 w-5 ${disabled ? "" : "cursor-pointer"}`}
        aria-checked={checked}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onBlur={handleBlur}
      />
      <label
        htmlFor={checkboxId}
        className={`relative flex h-5 w-5 items-center justify-center rounded border transition-all duration-200
          ${
            disabled
              ? "cursor-not-allowed surface-border bg-muted"
              : checked
              ? "cursor-pointer border-blue-500 bg-blue-500 hover:bg-blue-600 hover:border-blue-600"
              : "cursor-pointer border-gray-300 bg-white hover:border-gray-400 hover:bg-muted"
          }
          ${hasError && !disabled ? "border-red-300 hover:border-red-400" : ""}
          peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500/20 peer-focus-visible:ring-offset-2
        `}
        tabIndex={-1}
      >
        {checked && (
          <svg className={disabled ? "text-gray-400" : "text-white"} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" width="14" height="14">
            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </label>
      <label
        htmlFor={checkboxId}
        className={`flex h-full items-center text-sm font-medium select-none leading-relaxed
          ${disabled ? "cursor-not-allowed text-gray-500" : hasError ? "cursor-pointer text-red-600" : "cursor-pointer text-gray-900 hover:text-gray-700"}
        `}
      >
        {label}
      </label>
    </div>
  );
  return hasError && errorMsg ? <U2000ErrorTooltip message={typeof errorMsg === "string" ? errorMsg : JSON.stringify(errorMsg)}>{content}</U2000ErrorTooltip> : content;
};

export default U2000CheckBoxArray;
